import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';
import { CreditCard, CheckCircle2, XCircle } from 'lucide-react';

function redirectToPayfast({ payfast_url, fields }) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = payfast_url;
  Object.entries(fields).forEach(([k, v]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = k;
    input.value = v;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export default function BillingCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/billing/status');
      setStatus(res.data);
    } catch { /* not applicable for this role */ }
  }, []);

  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search).get('billing');
    if (q === 'success') toast.success('Payment received — your billing is being activated (confirmation may take a minute)');
    if (q === 'cancelled') toast.info('Payment cancelled — no charge was made');
  }, [load]);

  const subscribe = async (plan_code) => {
    setBusy(true);
    try {
      const res = await api.post('/billing/subscribe', { plan_code });
      redirectToPayfast(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start subscription'); setBusy(false); }
  };

  const setupInstitution = async () => {
    setBusy(true);
    try {
      const res = await api.post('/billing/institution/setup', { institution_type: status.institution_type });
      redirectToPayfast(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start billing setup'); setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel your FutureXV subscription?')) return;
    try {
      await api.post('/billing/cancel');
      toast.success('Subscription cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  if (!status || !status.role_kind) return null;
  const sub = status.subscription;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-6 mb-8" data-testid="billing-card">
      <div className="flex items-center gap-3 mb-4">
        <CreditCard className="w-5 h-5 text-primary-neon" />
        <h2 className="text-xl font-heading font-bold">Billing</h2>
        {sub && sub.status === 'active' && (
          <Badge className="bg-green-500/10 text-green-400 border border-green-500/30" data-testid="billing-active-badge">
            <CheckCircle2 className="w-3 h-3 mr-1" />Active
          </Badge>
        )}
        {sub && sub.status === 'pending' && (
          <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/30" data-testid="billing-pending-badge">Awaiting Payment Confirmation</Badge>
        )}
      </div>

      {status.role_kind === 'scout' && !sub && (
        <div>
          <p className="text-zinc-400 text-sm mb-4">Choose a scouting plan. Billed monthly in ZAR via PayFast — cancel anytime.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {status.plans.map((p) => (
              <div key={p.code} className="bg-zinc-950 border border-zinc-800 rounded-lg p-5">
                <p className="font-heading font-bold">{p.code === 'scout_lite' ? 'Scout Lite' : 'Scout Pro'}</p>
                <p className="text-primary-neon text-2xl font-heading font-bold mt-1">R{p.amount.toFixed(0)}<span className="text-zinc-500 text-sm font-normal"> / month</span></p>
                <p className="text-zinc-500 text-xs mt-2">{p.code === 'scout_lite' ? 'Province & position filtering, limited profile access' : 'Full national access, advanced filtering, messaging, exports'}</p>
                <Button onClick={() => subscribe(p.code)} disabled={busy} className="btn-primary w-full mt-4" data-testid={`subscribe-${p.code}`}>
                  Subscribe via PayFast
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {status.role_kind === 'institution' && !sub && (
        <div>
          <p className="text-zinc-400 text-sm">
            {status.institution_type?.charAt(0).toUpperCase() + status.institution_type?.slice(1)} tier: <span className="text-white font-bold">R{status.rate} / player / month</span>
            {' '}· Active players: <span className="text-white font-bold" data-testid="billing-player-count">{status.active_players}</span>
            {' '}· Monthly total: <span className="text-primary-neon font-bold" data-testid="billing-monthly-amount">R{status.monthly_amount?.toFixed(2)}</span>
          </p>
          <p className="text-zinc-500 text-xs mt-2">New institutions get a {status.trial_days}-day free trial. Activating billing charges the first month and securely stores your card with PayFast for monthly per-player billing.</p>
          <Button onClick={setupInstitution} disabled={busy} className="btn-primary mt-4" data-testid="activate-billing-button">
            Activate Billing via PayFast
          </Button>
        </div>
      )}

      {sub && (
        <div className="flex flex-wrap items-center gap-4">
          <p className="text-zinc-400 text-sm">
            {sub.kind === 'scout'
              ? `${sub.plan_code === 'scout_lite' ? 'Scout Lite' : 'Scout Pro'} — R${sub.amount?.toFixed(2)} / month`
              : `${sub.institution_type} billing — R${sub.rate} × ${sub.player_count} players = R${sub.amount?.toFixed(2)} / month`}
            {sub.next_billing_at && sub.status === 'active' && (
              <span className="text-zinc-600"> · next billing {new Date(sub.next_billing_at).toLocaleDateString()}</span>
            )}
          </p>
          {sub.status !== 'cancelled' && (
            <Button onClick={cancel} variant="outline" size="sm" className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="cancel-subscription-button">
              <XCircle className="w-4 h-4 mr-1.5" />Cancel
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
